'use client'

import { initiateIRacingOAuth } from '@/actions/iracing/initiate-oauth'
import { disconnectConnectedAccount } from '@/actions/user'
import { UnofficialAccounts } from '@/app/profile-manager/settings/connected-accounts/unofficial-accounts'
import Loader from '@/components/common/loader'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import connectedAccountOptions from '@/helpers/connected-account-options'
import customAccountOptions from '@/helpers/custom-account-options'
import { LinkIcon, PlusIcon, XIcon } from 'lucide-react'
import { signIn } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import React, { memo, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

// Discord specific row
function DiscordRow({ accountOption, user }) {
  const AccountDisplay = accountOption.displayTmpl

  if (user.discordUserId) {
    return (
      <TableRow>
        <TableCell className="text-p-3 font-semibold">Discord</TableCell>
        <TableCell className="h-24 w-full">
          {<AccountDisplay user={user} />}
        </TableCell>
        <TableCell />
      </TableRow>
    )
  }

  return (
    <TableRow>
      <TableCell className="h-full text-p-3 font-semibold">Discord</TableCell>
      <TableCell className="h-24 w-full text-p-3 font-semibold">
        Not connected
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => signIn('discord-link')}
          className="flex items-center justify-end gap-1 text-success"
        >
          <PlusIcon className="h-4 w-4 stroke-success" />
          Connect
        </button>
      </TableCell>
    </TableRow>
  )
}

// Memoized Helper Row
const AccountRow = memo(function AccountRow({
  accountOption,
  connectedAccounts,
  refreshData,
  session,
  handleDisconnect,
}) {
  const foundAccount = connectedAccounts.find(
    (ca) => ca.serviceId === accountOption.id,
  )

  const AccountDisplay = accountOption.displayTmpl
  const ConnectComponent = accountOption.connectComponent

  if (foundAccount) {
    return (
      <TableRow>
        <TableCell className="text-p-3 font-semibold">
          {accountOption.nicename}
        </TableCell>
        <TableCell className="h-24 w-full">
          {<AccountDisplay account={foundAccount} />}
          {accountOption.id === 'iracing' && foundAccount.metadata?.stale && (
            <div className="mt-1 text-xs text-warning">
              Action Required: Please verify your account connection.
            </div>
          )}
        </TableCell>
        <TableCell>
          {accountOption.id === 'iracing' && foundAccount.metadata?.stale ? (
            <button
              type="button"
              onClick={async () => {
                try {
                  const url = await initiateIRacingOAuth()
                  window.location.href = url
                } catch (e) {
                  toast.error('Failed to verify iRacing account')
                }
              }}
              className="flex items-center gap-1 text-warning"
            >
              <LinkIcon className="h-4 w-4 stroke-warning" />
              Verify
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleDisconnect(accountOption, foundAccount)}
              className="flex items-center gap-1 text-error"
            >
              <XIcon className="h-4 w-4 stroke-error" />
              Remove
            </button>
          )}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <TableCell className="h-full text-p-3 font-semibold">
        {accountOption.nicename}
      </TableCell>
      <TableCell className="h-24 w-full text-p-3 font-semibold">
        Not connected
      </TableCell>
      <TableCell>
        {ConnectComponent ? (
          <ConnectComponent
            session={session}
            customTrigger={
              <div className="flex items-center justify-end gap-1 text-success">
                <PlusIcon className="h-4 w-4 stroke-success" />
                Connect
              </div>
            }
            onIRacingLinked={() => refreshData()}
          />
        ) : (
          <button
            type="button"
            onClick={async () => {
              if (accountOption.id === 'iracing') {
                try {
                  const url = await initiateIRacingOAuth()
                  window.location.href = url
                } catch (e) {
                  toast.error('Failed to initiate iRacing login')
                }
                return
              }

              const currentUrl = new URL(window.location.href)
              signIn(accountOption.id, {
                callbackUrl: currentUrl.toString(),
              })
            }}
            className="flex items-center justify-end gap-1 text-success"
          >
            <PlusIcon className="h-4 w-4 stroke-success" />
            Connect
          </button>
        )}
      </TableCell>
    </TableRow>
  )
})

// Combined Options
const allAccountOptions = [
  ...connectedAccountOptions,
  ...customAccountOptions,
].sort((a, b) => a.id.localeCompare(b.id))

export default function ConnectedAccountsList({
  user,
  session,
  connectedAccounts: initialConnectedAccounts,
  profileLinks,
}) {
  const [isLoading, setIsLoading] = useState(false)
  const searchParams = useSearchParams()
  const router = useRouter()
  const error = searchParams.get('error')
  const success = searchParams.get('success')

  // We rely on Props for data, but we can trigger a refresh
  // Actually, Server Components + router.refresh() is the NextJS way.
  // But for immediate feedback, we might want local state too?
  // Let's stick to props + router.refresh() for simpler source of truth,
  // or use local state if we want optimistic updates.
  // For verify/disconnect, a refresh is safer to sync with DB.

  // Actually, prop updates from parent re-render won't happen unless parent re-renders.
  // router.refresh() triggers a server re-render of the page, dealing new props!

  useEffect(() => {
    if (error) {
      if (error === 'OAuthCallback') {
        toast.error(
          'Failed to connect account. It may be linked to another user.',
        )
      } else if (error === 'iracing_scope_undefined') {
        toast.error(
          'Failed to connect. Please ensure you have an active iRacing subscription.',
        )
      } else if (error === 'iracing_maintenance') {
        toast.error(
          'iRacing is currently down for maintenance. Please try again when iRacing services are back online.',
        )
      } else {
        toast.error(error)
      }
    }

    if (success === 'iracing') {
      toast.success('iRacing account linked successfully')
      router.refresh()
    }

    if (error || success) {
      router.replace('/profile-manager/settings/connected-accounts')
    }
  }, [error, success, router])

  async function handleDisconnect(service, account) {
    setIsLoading(`Disconnecting ${service.nicename}...`)
    try {
      await disconnectConnectedAccount(account.serviceId)
      toast.success(`Disconnected ${service.nicename}`)
      router.refresh()
    } catch (e) {
      console.error(e)
      toast.error(
        'Failed to disconnect account: ' +
          (e instanceof Error ? e.message : 'Unknown error'),
      )
    } finally {
      setIsLoading(false)
    }
  }

  function refreshData() {
    router.refresh()
  }

  return (
    <div className="relative space-y-6">
      {isLoading && <Loader size="overlay" content={isLoading} />}

      <Card className="bg-transparent! border-none p-0 shadow-none">
        <CardHeader className="px-0 pt-0">
          <CardTitle className="text-h-4 font-semibold text-white">
            Connected Accounts
          </CardTitle>
          <CardDescription className="text-p-1 text-neutral-blue-500">
            Manage all your connected accounts.
          </CardDescription>
        </CardHeader>

        <CardContent className="px-0">
          <Table>
            <TableBody>
              {allAccountOptions.map((option) => (
                <React.Fragment key={option.id}>
                  {option.id === 'discord-link' ? (
                    <DiscordRow accountOption={option} user={user} />
                  ) : (
                    <AccountRow
                      accountOption={option}
                      connectedAccounts={initialConnectedAccounts}
                      refreshData={refreshData}
                      session={session}
                      handleDisconnect={handleDisconnect}
                    />
                  )}
                  <TableRow className="h-px! p-0! last:hidden">
                    <TableCell className="p-0!">
                      <div className="divide bg-neutral-blue-700" />
                    </TableCell>
                    <TableCell className="p-0!">
                      <div className="divide bg-neutral-blue-700" />
                    </TableCell>
                    <TableCell className="p-0!">
                      <div className="divide bg-neutral-blue-700" />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UnofficialAccounts profileLinks={profileLinks} />
    </div>
  )
}
